<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div>
          <h3 class="label-input">Section Name</h3>
          <v-text-field v-model="fields.title" label="Name" />
        </div>
        <div class="mb-6">
          <h3 class="label-input">Title</h3>
          <ckeditor :editor="editor" v-model="fields.stepTitle" :config="editorConfig" />
        </div>
        <div class="mb-6">
          <h3 class="label-input">Image</h3>
          <v-select v-model="fields.image.position" :items="positionOptions" label="Position" />
          <UploadImage
            ref="imageUpload"
            @callback="handleUploadImage"
            :image="{ imageSrc: fields.image.url }"
            :height="`150`"
            :typeUpload="'image'"
            @callbackDelete="handleRemoveImage"
          />
        </div>
        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="label-input mb-0">Steps ({{ fields.steps.length }})</h3>
            <v-btn small color="primary" @click="handleAddStep">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Step
            </v-btn>
          </div>

          <div v-if="fields.steps.length === 0" class="text-center pa-4 grey--text">
            No steps added yet. Click "Add Step" to get started.
          </div>

          <v-expansion-panels v-else accordion>
            <v-expansion-panel v-for="(item, i) in fields.steps" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <div class="d-flex align-center">
                    <div class="step-number mr-3">{{ item.number }}:</div>
                    <span v-html="item.content.replace(/<[^>]*>/g, '').trim()"></span>
                  </div>
                  <v-btn icon small @click.stop="handleRemoveStep(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pa-2">
                  <v-text-field v-model="item.number" label="Step Number" />
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mt-6">
          <v-row>
            <v-col cols="6">
              <v-checkbox v-model="fields.topPadding" label="Top Padding" />
            </v-col>
            <v-col cols="6">
              <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" />
            </v-col>
          </v-row>
        </div>

        <div class="mt-6">
          <label class="label-input">Colors</label>
          <v-row>
            <v-col md="4" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="backgroundColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.backgroundColor"
                  @change="handleChangeBackgroundColor"
                />
                <v-text-field v-model="fields.colors.backgroundColor" label="Background Color" />
                <v-btn @click="handleResetBackgroundColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
            <v-col md="4" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="numberColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.numberColor"
                  @change="handleChangeNumberColor"
                />
                <v-text-field v-model="fields.colors.numberColor" label="Number Color" />
                <v-btn @click="handleResetNumberColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
            <v-col md="4" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="numberColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.borderColor"
                  @change="handleChangeBorderColor"
                />
                <v-text-field v-model="fields.colors.borderColor" label="Border Color" />
                <v-btn @click="handleResetBorderColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';

export default {
  name: 'cardStepSectionEditor',
  components: {
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      fields: {
        title: 'Card Step Section',
        stepTitle: '',
        image: {
          url: '',
        },
        steps: [
          {
            number: '01',
            content: '',
          },
        ],
        colors: {
          backgroundColor: '#ffffff',
          numberColor: '#90cf5b',
          borderColor: '#BCBCBC',
        },
        topPadding: true,
        bottomPadding: true,
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardStep(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },

    // Review image handlers
    handleUploadImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.image.url = obj.imageSrc;
      }
    },

    handleRemoveImage() {
      this.fields.image.url = '';
    },

    // List item handlers
    handleAddStep() {
      this.fields.steps.push({
        number: '01',
        content: 'Step Content',
      });
    },
    handleRemoveStep(index) {
      this.fields.steps.splice(index, 1);
    },
    handleChangeBackgroundColor(event) {
      this.fields.colors.backgroundColor = event.target.value;
    },
    handleResetBackgroundColor() {
      this.fields.colors.backgroundColor = '#ffffff';
    },
    handleChangeNumberColor(event) {
      this.fields.colors.numberColor = event.target.value;
    },
    handleResetNumberColor() {
      this.fields.colors.numberColor = '#90cf5b';
    },
    handleChangeBorderColor(event) {
      this.fields.colors.borderColor = event.target.value;
    },
    handleResetBorderColor() {
      this.fields.colors.borderColor = '#BCBCBC';
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 150px !important;
}
::v-deep .ck-editor__editable {
  min-height: 150px !important;
}

.label-input {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.gap-3 {
  gap: 12px;
}

.feature-thumbnail {
  width: 50px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}

::v-deep .v-textarea {
  &.v-text-field--outlined {
    .v-input__control {
      .v-input__slot {
        min-height: 80px;
      }
    }
  }
}
</style>
